import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import {
  LayoutDashboard,
  DollarSign,
  Receipt,
  Building2,
  TrendingUp,
  Shield,
  MessageSquare,
  LogOut,
  ChevronRight,
  Menu,
  X,
  Briefcase,
  Calculator,
  Calendar,
  PieChart,
  Brain,
  Settings,
  Search,
  CreditCard,
  Target,
  FileBarChart,
  Wallet,
  ArrowDownUp,
  Gauge,
  RefreshCw,
  Heart,
  Camera,
  CalendarDays,
  TrendingDown,
  BookOpen,
  Sparkles,
  FileText,
  ScanLine
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";

import NotificationBell from "./NotificationBell";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/net-worth", label: "Net Worth", icon: TrendingUp },
  { path: "/income", label: "Income", icon: DollarSign },
  { path: "/expenses", label: "Expenses", icon: Receipt },
  { path: "/loans", label: "Loans & HELOC", icon: Wallet },
  { path: "/cash-flow", label: "Cash Flow", icon: ArrowDownUp },
  { path: "/budget", label: "Budget Planner", icon: Gauge },
  { path: "/recurring", label: "Recurring", icon: RefreshCw },
  { path: "/health-score", label: "Health Score", icon: Heart },
  { path: "/receipt-scanner", label: "Receipts", icon: Camera },
  { path: "/financial-calendar", label: "Calendar", icon: CalendarDays },
  { path: "/debt-payoff", label: "Debt Payoff", icon: TrendingDown },
  { path: "/reports", label: "Reports", icon: FileBarChart },
  { path: "/credit-cards", label: "Credit Cards", icon: CreditCard },
  { path: "/real-estate", label: "Real Estate", icon: Building2 },
  { path: "/rent-dashboard", label: "Rent Portfolio", icon: PieChart },
  { path: "/real-estate-research", label: "RE Research", icon: Search },
  { path: "/deal-finder", label: "Deal Finder", icon: Target },
  { path: "/business", label: "Business", icon: Briefcase },
  { path: "/investments", label: "Investments", icon: TrendingUp },
  { path: "/goals", label: "Goals", icon: Target },
  { path: "/insurance", label: "Life Insurance", icon: Shield },
  { path: "/tax-center", label: "Tax Center", icon: Calculator },
  { path: "/tax-education", label: "Tax Education", icon: BookOpen },
  { path: "/tax-advisor", label: "Tax Advisor AI", icon: Sparkles },
  { path: "/form-filler", label: "Form Filler", icon: FileText },
  { path: "/tax-ocr", label: "Document OCR", icon: ScanLine },
  { path: "/calendar", label: "Payment Calendar", icon: Calendar },
  { path: "/ai-advisor", label: "AI Advisor", icon: Brain },
  { path: "/automation", label: "Automation", icon: Settings },
  { path: "/ai-assistant", label: "AI Assistant", icon: MessageSquare },
];

export default function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-[#050505] overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#0A0A0A] border-r border-white/10
        transform transition-transform duration-200 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-lg text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>
                WealthHub
              </span>
            </Link>
          </div>

          {/* Nav items */}
          <nav className="flex-1 py-4 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                  className={`
                    flex items-center gap-3 px-6 py-3 text-sm font-medium transition-all
                    ${isActive 
                      ? 'bg-primary/10 text-primary border-l-2 border-primary' 
                      : 'text-zinc-400 hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full justify-start text-zinc-400 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-zinc-400 hover:text-white"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                {title}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6 grid-bg min-h-[calc(100vh-73px)]">
          {children}
        </div>
      </main>
    </div>
  );
}
